import React, { createContext, useContext, useState, useEffect } from 'react';

interface SidebarContextType {
  isCollapsed: boolean;
  isHovered: boolean;
  sidebarWidth: number;
}

const SidebarContext = createContext<SidebarContextType>({
  isCollapsed: false,
  isHovered: false,
  sidebarWidth: 256 // 64 * 4 (tailwind units to pixels)
});

export const useSidebar = () => useContext(SidebarContext);

export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  
  // Listen for sidebar state changes via custom events
  useEffect(() => {
    const handleSidebarToggle = (e: CustomEvent) => {
      setIsCollapsed(e.detail.isCollapsed);
    };
    
    const handleSidebarHover = (e: CustomEvent) => {
      setIsHovered(e.detail.isHovered);
    };
    
    window.addEventListener('sidebar-toggle' as any, handleSidebarToggle as EventListener);
    window.addEventListener('sidebar-hover' as any, handleSidebarHover as EventListener);
    
    return () => {
      window.removeEventListener('sidebar-toggle' as any, handleSidebarToggle as EventListener);
      window.removeEventListener('sidebar-hover' as any, handleSidebarHover as EventListener);
    };
  }, []);
  
  // Calculate sidebar width in pixels based on state
  const sidebarWidth = isCollapsed 
    ? (isHovered ? 256 : 64) // 16rem = 256px, 4rem = 64px
    : 256; // 16rem = 256px
  
  return (
    <SidebarContext.Provider value={{ isCollapsed, isHovered, sidebarWidth }}>
      {children}
    </SidebarContext.Provider>
  );
};
